import { addScribbleScore } from "../functions/addScribbleScore";
import { syncScribbleScoreToExtraMile } from "./scribbleExtraMile";

const GAME_START_KEY = "scribbleGameStartedAt";

export function markScribbleGameStarted() {
  if (!sessionStorage.getItem(GAME_START_KEY)) {
    sessionStorage.setItem(GAME_START_KEY, String(Date.now()));
  }
}

export function getScribbleElapsedSec() {
  const raw = sessionStorage.getItem(GAME_START_KEY);
  const start = raw ? Number(raw) : Date.now();
  return Math.max(0, Math.floor((Date.now() - start) / 1000));
}

export function clearScribbleGameStarted() {
  sessionStorage.removeItem(GAME_START_KEY);
}

function readMaxPointsFromSettings() {
  try {
    const gs = JSON.parse(sessionStorage.getItem("gameSettings") || "{}") || {};
    const p = Number(gs.maxPoints ?? gs.points);
    if (Number.isFinite(p) && p >= 1) return Math.min(100, Math.max(1, Math.round(p)));
  } catch {
    /* ignore */
  }
  return 100;
}

/** Points scale with time left: up to maxPoints (from host settings) when the timer is full. */
export function pointsForCorrectGuess(remainingSec, timerSeconds) {
  const cap = readMaxPointsFromSettings();
  const total = Math.max(1, Number(timerSeconds) || 60);
  const left = Math.max(0, Number(remainingSec) || 0);
  return Math.max(1, Math.round((left / total) * cap));
}

/** Drawer earns 50% of the guesser's points (rounded). */
export function pointsForDrawerOnCorrect(guesserPoints) {
  const pts = Math.max(0, Math.round(Number(guesserPoints) || 0));
  return Math.round(pts * 0.5);
}

/** Lobby id for leaderboard / scoring (route state → gameSettings → userData). */
export function resolveActiveLobbyName(routeLobbyName) {
  const fromRoute =
    routeLobbyName != null && String(routeLobbyName).trim() !== ""
      ? String(routeLobbyName).trim()
      : "";
  if (fromRoute) return fromRoute;
  return readScribbleApiContext().lobby_name;
}

export function readScribbleApiContext() {
  let user = {};
  let gs = {};
  try {
    user = JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    user = {};
  }
  try {
    gs = JSON.parse(sessionStorage.getItem("gameSettings") || "{}") || {};
  } catch {
    gs = {};
  }

  const userId = String(user.userId ?? user.userid ?? user.id ?? "").trim();
  const sessionId = user.sessionId;
  const organizationId = user.organizationId;
  const token =
    sessionId && organizationId ? `${sessionId}&${organizationId}` : "";

  return {
    userId,
    token,
    lobby_name: String(gs.lobby_name ?? user.lobby_name ?? user.lobby ?? "").trim(),
    stageId: gs.stageId ?? null,
    themeName: gs.themeName ?? "guess_me",
  };
}

export async function recordCorrectGuess({
  guessText,
  turnIndex,
  remainingSec,
  timerSeconds,
  isGameComplete,
  points,
}) {
  const ctx = readScribbleApiContext();
  if (!ctx.token || !ctx.userId || !ctx.lobby_name) return null;

  const earned =
    points != null
      ? Math.max(0, Math.round(Number(points) || 0))
      : pointsForCorrectGuess(remainingSec, timerSeconds);

  const result = await addScribbleScore({
    token: ctx.token,
    userId: ctx.userId,
    lobby_name: ctx.lobby_name,
    points: earned,
    themeName: ctx.themeName,
    stageId: ctx.stageId,
    turnIndex,
    guessText,
    isGameComplete,
  });

  try {
    sessionStorage.setItem("scribbleLastTotalScore", String(result.total_score ?? 0));
  } catch {
    /* ignore */
  }

  if (earned > 0) {
    await syncScribbleScoreToExtraMile(result.total_score);
  }

  return { ...result, points: earned };
}

/** Award points to the drawer when someone guesses correctly. */
export async function recordDrawerBonus({ drawerUserId, turnIndex, points }) {
  const ctx = readScribbleApiContext();
  if (!ctx.token || !ctx.lobby_name) return null;
  const drawerId = String(drawerUserId ?? "").trim();
  if (!drawerId) return null;

  const earned = Math.max(0, Math.round(Number(points) || 0));
  if (earned <= 0) return null;

  const result = await addScribbleScore({
    token: ctx.token,
    userId: drawerId,
    lobby_name: ctx.lobby_name,
    points: earned,
    themeName: ctx.themeName,
    turnIndex,
    guessText: "",
    role: "drawer_bonus",
  });

  if (drawerId === ctx.userId && earned > 0) {
    await syncScribbleScoreToExtraMile(result?.total_score);
  }

  return result;
}

/** Mark player session complete in DB and sync final score to Extra Mile report. */
export async function finalizeScribblePlayer() {
  const ctx = readScribbleApiContext();
  if (!ctx.token || !ctx.userId || !ctx.lobby_name) return null;

  const result = await addScribbleScore({
    token: ctx.token,
    userId: ctx.userId,
    lobby_name: ctx.lobby_name,
    points: 0,
    themeName: ctx.themeName,
    stageId: ctx.stageId,
    isGameComplete: true,
    record_time: true,
    time_sec: getScribbleElapsedSec(),
  });

  await syncScribbleScoreToExtraMile(result?.total_score ?? 0);

  clearScribbleGameStarted();
  return result;
}
